import { log } from "@/lib/logger";
import { queryOptions } from "@/lib/query";

import { apiClient, ApiError } from "./client";
import type {
  Booking,
  BookingStatus,
  Doctor,
  DoctorStats,
  RegisterDoctorInput,
  UpdateDoctorInput,
} from "@/types/doctor";

const BOOKINGS_PATH = "/doctors/me/bookings";

/**
 * The statuses that exist in the deployed database enum.
 *
 * Deliberately excludes `missed`: staging's `BookingStatus` enum has not had it
 * added yet, which is the whole reason the unfiltered list route throws.
 */
const DEPLOYED_STATUSES: BookingStatus[] = ["booked", "completed", "cancelled"];

/**
 * Set once the per-status retry has been shown not to help.
 *
 * Without this, a controller that ignores the `status` param turns every failed
 * load into four failed requests — and SWR's error retries multiply that again.
 * Cleared by a reload, which is also when it would be worth re-testing.
 */
let fallbackIsUseless = false;

/**
 * Fetches the doctor's bookings, with a fallback for a broken list route.
 *
 * Staging currently 500s on the unfiltered route: the deployed §4 code queries
 * `BookingStatus.missed` while the database enum still only has
 * `booked | completed | cancelled`, so `prisma.booking.findMany()` throws before
 * returning a row (see `backend_changes.md` §0a — the real fix is one migration).
 *
 * When that happens, this asks for each status the database *does* know about
 * and merges the results. It only helps if the controller honours a `status`
 * query param — if it doesn't, those calls fail the same way and the original
 * error is rethrown, so the screen is no worse off than before. The three
 * statuses cover every row that can exist while the enum is short one value, so
 * a successful fallback is a complete list, not a partial one.
 *
 * This is a workaround with an expiry date: delete it once staging carries the
 * migration and the primary route returns 200 again.
 */
async function fetchBookings(signal?: AbortSignal): Promise<Booking[]> {
  try {
    const bookings = await apiClient.get<Booking[]>(BOOKINGS_PATH, signal);
    // A non-array here would crash classification downstream with a far less
    // obvious message than the request failing outright.
    if (!Array.isArray(bookings)) {
      throw new ApiError(502, "The server returned an unexpected bookings payload.");
    }
    return bookings;
  } catch (error) {
    if (!(error instanceof ApiError) || error.status < 500 || fallbackIsUseless) throw error;

    log.warn("api", "bookings.fallback", {
      msg: "Unfiltered bookings route failed; retrying per status",
      status: error.status,
    });

    const perStatus = await Promise.all(
      DEPLOYED_STATUSES.map((status) =>
        apiClient
          .get<Booking[]>(`${BOOKINGS_PATH}?status=${status}`, signal)
          .then((rows) => (Array.isArray(rows) ? rows : null))
          .catch(() => null),
      ),
    );

    if (perStatus.every((rows) => rows === null)) {
      fallbackIsUseless = true;
      log.warn("api", "bookings.fallbackFailed", {
        msg:
          "Per-status bookings requests failed too — the status param is not honoured. " +
          "The database enum migration (backend_changes.md §0a) is the only fix.",
      });
      throw error;
    }

    // Dedupe by id: a controller that ignores the param returns the same full
    // list three times over.
    const byId = new Map<string, Booking>();
    for (const rows of perStatus) {
      for (const booking of rows ?? []) byId.set(booking.id, booking);
    }
    const merged = [...byId.values()];

    log.info("api", "bookings.fallbackOk", { count: merged.length });
    return merged;
  }
}

/** Every endpoint in the documented doctor-facing contract. Nothing else. */
export const doctorApi = {
  register: (input: RegisterDoctorInput) =>
    apiClient.post<{ message: string; doctor: Doctor }>("/doctors/register", input),

  me: (signal?: AbortSignal) => apiClient.get<Doctor>("/doctors/me", signal),

  updateMe: (input: UpdateDoctorInput) =>
    apiClient.put<{ message: string; doctor: Doctor }>("/doctors/me", input),

  bookings: (signal?: AbortSignal) => fetchBookings(signal),

  stats: (signal?: AbortSignal) => apiClient.get<DoctorStats>("/doctors/me/stats", signal),

  /**
   * Cancels a booking.
   *
   * `DELETE /bookings/:id` is the backend's cancellation route — it is the only
   * booking-mutating endpoint the API exposes (probed: every `/cancel` and
   * `/status` variant 404s, this one authenticates). The list is refetched
   * afterwards rather than patched locally, so whatever status the backend
   * settles on is what the Cancelled filter shows.
   *
   * Failures carry the backend's own `message`, which the UI shows verbatim.
   */
  cancelBooking: (bookingId: string) =>
    apiClient.del<CancelBookingResponse>(`/bookings/${bookingId}`),
};

export interface CancelBookingResponse {
  message?: string;
  booking?: Booking;
}

export const doctorKeys = {
  me: ["doctor", "me"] as const,
  bookings: ["doctor", "bookings"] as const,
  stats: ["doctor", "stats"] as const,
};

export const meQuery = (uid: string | null) =>
  queryOptions({
    queryKey: [...doctorKeys.me, uid],
    queryFn: () => doctorApi.me(),
    enabled: Boolean(uid),
    retry: false,
    staleTime: 30_000,
  });

export const bookingsQuery = (uid: string | null) =>
  queryOptions({
    queryKey: [...doctorKeys.bookings, uid],
    queryFn: () => doctorApi.bookings(),
    enabled: Boolean(uid),
    staleTime: 30_000,
  });

export const statsQuery = (uid: string | null) =>
  queryOptions({
    queryKey: [...doctorKeys.stats, uid],
    queryFn: () => doctorApi.stats(),
    enabled: Boolean(uid),
    staleTime: 30_000,
  });
